import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Select,
  message,
  Card,
  Row,
  Col,
  Form,
  Typography
} from 'antd';
import {
  PlusOutlined,
  FilterOutlined,
  UserOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { all_routes } from '../../../router/all_routes';
import userService from '../../../services/user/user.service';
import vehicleService from '../../../services/vehicle/vehicle.service';
import departmentService from '../../../services/department/department.service';
import shopService from '../../../services/shop/shop.service';
import fieldActivityService from '../../../services/field-activity/field-activity.service';
import { Vehicle } from '../../../types/vehicle.types';
import { User, EmploymentType } from '../../../types/user.types';
import { CreateFieldActivityDTO, ActivityStatus } from '../../../types/field.types';
import { handleApiError } from '../../../utils/errorHandler';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import UserSelectionCard from './components/UserSelectionCard';
import GeneralActivityFormModal from './components/GeneralActivityFormModal';

const { Option } = Select;
const { Title, Text } = Typography;

const PersonelSelectionPage: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const navigate = useNavigate();
  const [generalCreateForm] = Form.useForm();

  // Data States
  const [users, setUsers] = useState<User[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal States
  const [generalCreateModalVisible, setGeneralCreateModalVisible] = useState(false);

  // User Selection Filters
  const [userEmployeeTypeFilter, setUserEmployeeTypeFilter] = useState<EmploymentType | undefined>(undefined);
  const [userGenderFilter, setUserGenderFilter] = useState<string | undefined>(undefined);
  const [userDepartmentFilter, setUserDepartmentFilter] = useState<number | undefined>(undefined);
  const [userBranchFilter, setUserBranchFilter] = useState<number | undefined>(undefined);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  useEffect(() => {
    fetchUsers();
    fetchVehicles();
    fetchDepartments();
    fetchBranches();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, userEmployeeTypeFilter, userGenderFilter, userDepartmentFilter, userBranchFilter]);

  const fetchUsers = async () => {
    try {
      const userData = await userService.getUsers();
      const activeUsers = userData.filter((user) => 
        user.status === 'active' && user.role !== 'admin'
      );
      setUsers(activeUsers);
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (userEmployeeTypeFilter) {
      filtered = filtered.filter(user => user.employeeType === userEmployeeTypeFilter);
    }

    if (userGenderFilter) {
      filtered = filtered.filter(user => user.gender === userGenderFilter);
    }

    if (userDepartmentFilter) {
      filtered = filtered.filter(user => 
        user.primaryDepartment?.id === userDepartmentFilter ||
        user.department?.some(dept => dept.id === userDepartmentFilter)
      );
    }

    if (userBranchFilter) {
      filtered = filtered.filter(user => 
        user.branch?.some(b => b.id === userBranchFilter)
      );
    }

    setFilteredUsers(filtered);
  };

  const fetchVehicles = async () => {
    try {
      const vehicleData = await vehicleService.getVehicles();
      setVehicles(vehicleData);
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const fetchDepartments = async () => {
    try {
      const departmentData = await departmentService.getDepartments();
      setDepartments(departmentData);
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const fetchBranches = async () => {
    try {
      const branchData = await shopService.getShops();
      setBranches(branchData);
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleUserSelect = (user: User) => {
    navigate(`${routes.personelActivities}/${user.id}`, {
      state: { user }
    });
  };

  const handleGeneralCreateActivity = async (values: any) => {
    try {
      // Modal tarih alanını "dates", personeli tek değer olarak gönderiyor
      const range = values.dates || values.dateRange;
      const activityData: CreateFieldActivityDTO = {
        title: values.title,
        description: values.description,
        type: values.type,
        startDate: range[0].toISOString(),
        endDate: range[1].toISOString(),
        userIds: Array.isArray(values.userIds) ? values.userIds : [values.userIds],
        isBillable: values.isBillable || false,
        vehicleId: values.vehicleId || undefined
      };

      await fieldActivityService.createFieldActivity(activityData);
      message.success('Aktivite başarıyla oluşturuldu!');
      setGeneralCreateModalVisible(false);
      generalCreateForm.resetFields();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  return (
    <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
      <div className="content flex-fill h-100">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">
              <UserOutlined /> Personel Seçimi
            </h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Yönetim</li>
                <li className="breadcrumb-item active">Personel Aktiviteleri</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setGeneralCreateModalVisible(true)}
              size="large"
            >
              Aktivite Oluştur
            </Button>
          </div>
        </div>

        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
          <Card style={{ marginBottom: '24px', textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Title level={3} style={{ color: 'white', margin: 0 }}>
              <EnvironmentOutlined /> Takip Etmek İstediğiniz Personeli Seçin
            </Title>
            <Text style={{ color: 'white', opacity: 0.9 }}>
              Seçtiğiniz personelin aktivitelerini görüntüleyebilirsiniz
            </Text>
          </Card>

          {/* Filtreler */}
          <Card style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Çalışan Tipi"
                  style={{ width: '100%' }}
                  value={userEmployeeTypeFilter}
                  onChange={setUserEmployeeTypeFilter}
                  allowClear
                >
                  <Option value={EmploymentType.FIELD}>🗺️ Saha Çalışanı</Option>
                  <Option value={EmploymentType.OFFICE}>🏢 Ofis Çalışanı</Option>
                  <Option value={EmploymentType.HYBRID}>🔀 Hibrit Çalışan</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Cinsiyet"
                  style={{ width: '100%' }}
                  value={userGenderFilter}
                  onChange={setUserGenderFilter}
                  allowClear
                >
                  <Option value="male">👨 Erkek</Option>
                  <Option value="female">👩 Kadın</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Departman"
                  style={{ width: '100%' }}
                  value={userDepartmentFilter}
                  onChange={setUserDepartmentFilter}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {departments.map((dept) => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Şube"
                  style={{ width: '100%' }}
                  value={userBranchFilter}
                  onChange={setUserBranchFilter}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {branches.map((branch) => (
                    <Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            {(userEmployeeTypeFilter || userGenderFilter || userDepartmentFilter || userBranchFilter) && (
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary">
                  <FilterOutlined /> {filteredUsers.length} personel bulundu
                </Text>
                <Button
                  size="small"
                  onClick={() => {
                    setUserEmployeeTypeFilter(undefined);
                    setUserGenderFilter(undefined);
                    setUserDepartmentFilter(undefined);
                    setUserBranchFilter(undefined);
                  }}
                >
                  Filtreleri Temizle
                </Button>
              </div>
            )}
          </Card>

          {loading ? (
            <LoadingSpinner centered />
          ) : (
            <Row gutter={[24, 24]}>
              {((userEmployeeTypeFilter || userGenderFilter || userDepartmentFilter || userBranchFilter) 
                ? filteredUsers 
                : users
              ).map((user) => (
                <Col xs={24} sm={12} md={8} lg={6} key={user.id}>
                  <UserSelectionCard user={user} onSelect={handleUserSelect} />
                </Col>
              ))}
            </Row>
          )}

          {(filteredUsers.length === 0 && (userEmployeeTypeFilter || userGenderFilter || userDepartmentFilter || userBranchFilter)) && (
            <Card style={{ textAlign: 'center', padding: '40px' }}>
              <FilterOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <Title level={4} type="secondary">Filtre Sonucu Bulunamadı</Title>
              <Text type="secondary">
                Seçtiğiniz filtrelere uygun personel bulunamadı. Filtreleri değiştirmeyi deneyin.
              </Text>
            </Card>
          )}

          {users.length === 0 && !loading && (
            <Card style={{ textAlign: 'center', padding: '40px' }}>
              <UserOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <Title level={4} type="secondary">Personel Bulunamadı</Title>
              <Text type="secondary">
                Sistemde kayıtlı personel bulunmamaktadır.
              </Text>
            </Card>
          )}
        </div>
      </div>
      
      {/* General Create Activity Modal */}
      <GeneralActivityFormModal
        visible={generalCreateModalVisible}
        onCancel={() => {
          setGeneralCreateModalVisible(false);
          generalCreateForm.resetFields();
        }}
        onFinish={handleGeneralCreateActivity}
        form={generalCreateForm}
        users={users}
        vehicles={vehicles}
        title="Genel Aktivite Ataması"
      />
    </div>
  );
};

export default PersonelSelectionPage;

